import {
  Accordion as ChakraAccordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Grid,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  Tooltip
} from '@chakra-ui/react';
import {
  Avatar,
  Button,
  ButtonIcon,
  ButtonText,
  Modal,
  OutlineCard,
  SearchInput,
  useToast
} from '@morressier/alembic';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSubmissionAssignment } from 'src/api/commands';
import { useSubmissionData } from '../../contexts/SubmissionDataProvider';
import { useKeycloak } from 'src/components/AuthHOC';
import ComposeEmailModal, {
  AdHocModalOpen
} from 'src/components/Submission/AuthorsTab/ComposeEmailModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faExclamationTriangle,
  faChevronDown,
  faPenToSquare,
  faPlus,
  faTrash,
  faXmark
} from '@fortawesome/pro-regular-svg-icons';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
import { useCOCContextData } from 'src/contexts/COCDataProvider';
import { ISubmissionWFState } from '@publishing-engine/domain/types/workflow-phases';
import { cocAllowedStates } from './RecommendationAction';
import ProxyUser from 'src/components/ProxyUser/ProxyUser';
import { DividerVertical } from 'src/components/Submission/OverviewTab';
import Divider from 'src/components/Divider';
import { faUser } from '@fortawesome/pro-solid-svg-icons';

interface Props {
  submissionId: string;
  readOnly?: boolean;
  defaultExpanded?: boolean;
}
type RoleAssignment = {
  role: string;
  users: { id: string; firstName: string; lastName: string; email: string }[];
  assigneeId: string | undefined;
  isValidUser: boolean;
};
type UnassignRoleAssignment = {
  role: string;
  assigneeId: string | undefined;
  assigneeName: string;
};

export const cocUnassignStates = [
  'peerReviewPending',
  'peerReviewInvitation',
  'peerReviewFinished',
  'revisionAssessment',
  'revisionPeerReviewInvitation',
  'revisionPeerReviewPending',
  'revisionPeerReviewFinished'
];

export const avatarBackgroundByRoleLevel: Record<number, string> = {
  1: '#4C5FD5',
  2: '#2F8F7A',
  3: '#C97A3D'
};

export const COCSubmissionAssignment: React.FC<Props> = ({
  submissionId,
  readOnly = false,
  defaultExpanded = true
}) => {
  const toast = useToast();
  const {
    refetchSubmission,
    organizationId,
    workflowResponse,
    isDecisionMaker
  } = useSubmissionData();
  const { currentUser } = useKeycloak();
  const { ctxId = '' } = useParams();
  const { submissionContext } = useSubmissionContextData();
  const { rolesAssignments, recommendationAssignees } = useCOCContextData();
  const [openSendEmailModal, setOpenSendEmailModal] = useState<AdHocModalOpen>({
    status: false,
    defaultEmail: []
  });
  const [assigningRole, setAssigningRole] = useState<RoleAssignment | null>(
    null
  );
  const [unassigningRole, setUnassigningRole] =
    useState<UnassignRoleAssignment | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editAssigneeSearchTerm, setEditAssigneeSearchTerm] = useState('');
  const [isEditAssigneeDropdownOpen, setIsEditAssigneeDropdownOpen] =
    useState(false);
  const [editingRole, setEditingRole] = useState<RoleAssignment | null>(null);
  const [editSelectedAssigneeId, setEditSelectedAssigneeId] = useState('');

  useEffect(() => {
    if (assigningRole) {
      setSearchTerm('');
    }
  }, [assigningRole]);

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return assigningRole?.users ?? [];
    return (assigningRole?.users ?? []).filter(
      user =>
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term)
    );
  }, [assigningRole?.users, searchTerm]);

  const filteredEditAssignees = useMemo(() => {
    const term = editAssigneeSearchTerm.trim().toLowerCase();
    if (!term) return editingRole?.users ?? [];
    return (editingRole?.users ?? []).filter(
      user =>
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term)
    );
  }, [editingRole?.users, editAssigneeSearchTerm]);

  const manageSubmissionAssignmentMutation = useSubmissionAssignment({
    submissionContextId: ctxId,
    submissionId,
    organizationId,
    invalidateSubmissionQueries: refetchSubmission
  });

  const submissionCurrentState = workflowResponse.workflowData
    .currentState as ISubmissionWFState;
  const isInitialAssessmentPhase =
    submissionCurrentState === 'initialAssessment';
  const isRevisionPhase = submissionCurrentState === 'revision';
  const cocConfigOwner = submissionContext?.config.cocConfig?.find(
    user => user.isOwner
  )?.role;
  const hasApproveForPeerReviewDecision =
    workflowResponse.articleWorkflow.transitionGraph.submission.states[
      submissionCurrentState
    ]?.actions?.some(
      action =>
        action.name === 'EditorialSubmissionDecision#approveForPeerReview'
    ) ?? false;

  const isChecklistPhase = ['assignment', 'initialAssessment'].includes(
    submissionCurrentState
  );

  const isCoCPhase = cocAllowedStates.includes(submissionCurrentState);

  const onAssignSubmission = async (assigneeId?: string, role?: string) => {
    if (!assigneeId && !role) return;

    const res = await manageSubmissionAssignmentMutation.mutateAsync({
      assigneeId: assigneeId ?? '',
      role: role ?? '',
      type: role === cocConfigOwner ? 'COCAssignSubmission' : 'AssignCOCEditor'
    });

    if (res.isSuccess) {
      return toast({
        variant: 'success',
        title: `${role} Assigned.`,
        isClosable: true
      });
    }

    return toast({
      variant: 'error',
      title: res.message,
      isClosable: true
    });
  };
  const onUnassignRole = async (assigneeId?: string, role?: string) => {
    if (!assigneeId && !role) return;

    const res = await manageSubmissionAssignmentMutation.mutateAsync({
      assigneeId: assigneeId ?? '',
      role: role ?? '',
      type: 'UnassignCOCRole'
    });

    if (res.isSuccess) {
      setUnassigningRole(null);
      return toast({
        variant: 'success',
        title: `${role} Unassigned.`,
        isClosable: true
      });
    }

    return toast({
      variant: 'error',
      title: res.message,
      isClosable: true
    });
  };

  const onSaveEditRole = async () => {
    if (!editingRole) return;

    const newAssigneeId = editSelectedAssigneeId;

    if (!newAssigneeId || newAssigneeId === editingRole.assigneeId) {
      setEditingRole(null);
      setEditSelectedAssigneeId('');
      setEditAssigneeSearchTerm('');
      setIsEditAssigneeDropdownOpen(false);
      return;
    }

    const assignRes = await manageSubmissionAssignmentMutation.mutateAsync({
      assigneeId: newAssigneeId,
      role: editingRole.role,
      replacesAssigneeId: editingRole.assigneeId,
      type:
        editingRole.role === cocConfigOwner
          ? 'COCAssignSubmission'
          : 'AssignCOCEditor'
    });

    if (assignRes.isSuccess) {
      setEditingRole(null);
      setEditSelectedAssigneeId('');
      setEditAssigneeSearchTerm('');
      setIsEditAssigneeDropdownOpen(false);
      toast({
        variant: 'success',
        title: `${editingRole.role} updated.`,
        isClosable: true
      });
      return;
    }

    toast({
      variant: 'error',
      title: assignRes.message,
      isClosable: true
    });
  };

  const cocConfig = submissionContext?.config.cocConfig;
  const getAvatarBackground = (role: string) =>
    avatarBackgroundByRoleLevel[
      cocConfig?.find(item => item.role === role)?.roleLevel ?? 1
    ] ?? '#4C5FD5';
  const isOwnerAssign = recommendationAssignees?.recommendationAssignees?.some(
    item => item.role === cocConfigOwner
  );
  const proxyUserDisabled = (assigneeId: string) =>
    recommendationAssignees?.recommendationAssignees?.some(
      item => item.userId === assigneeId && !item.isProxyUser
    );
  const isAnyModalOpen =
    !!assigningRole ||
    !!unassigningRole ||
    !!editingRole ||
    openSendEmailModal.status;
  const currentlyAssignedUsers = (assigningRole?.users ?? []).filter(user =>
    recommendationAssignees?.recommendationAssignees?.some(
      assignment =>
        assignment.role === assigningRole?.role && assignment.userId === user.id
    )
  );
  const assigningRoleMaxLimit =
    submissionContext?.config.cocConfig?.find(
      item => item.role === assigningRole?.role
    )?.roleMaxLimit ?? 1;
  const assigningRoleAssignedCount =
    recommendationAssignees?.recommendationAssignees?.filter(
      item => item.role === assigningRole?.role
    ).length ?? 0;
  const canAddToAssigningRole =
    assigningRoleAssignedCount < assigningRoleMaxLimit;
  const findRoleConfig = (role: string) =>
    submissionContext?.config.cocConfig?.find(item => item.role === role);
  return (
    <>
      {!readOnly && unassigningRole && (
        <Modal
          isOpen
          onClose={() => {}}
          closeButtonProps={{ hidden: true }}
          width="448px"
          data-cy="confirmation-modal"
        >
          <Flex padding={8} gap={6} flexDirection="column">
            <Flex alignItems="center" gap={4}>
              <Box color="actionSecondaryCautionContent">
                <FontAwesomeIcon fontSize={32} icon={faExclamationTriangle} />
              </Box>
              <Text textStyle="headingGroup">Unassign User?</Text>
            </Flex>
            <Text textStyle="textBody">
              Are you sure you want to unassign{' '}
              <b>{unassigningRole.assigneeName}</b> from the{' '}
              <b>{unassigningRole?.role}</b> role for this submission?
            </Text>

            <Flex gap={2} marginTop={6} justifyContent="flex-end">
              <Button
                onClick={() => {
                  setUnassigningRole(null);
                }}
                variant="secondary"
                data-cy="confirmation-modal-cancel-button"
              >
                cancel
              </Button>
              <Button
                type="submit"
                variant="caution"
                onClick={() =>
                  onUnassignRole(
                    unassigningRole?.assigneeId,
                    unassigningRole?.role
                  )
                }
                data-cy="confirmation-modal-close-button"
              >
                Unassign
              </Button>
            </Flex>
          </Flex>
        </Modal>
      )}
      {!readOnly && editingRole && (
        <Modal
          isOpen={!!editingRole}
          onClose={() => {
            setEditingRole(null);
            setEditSelectedAssigneeId('');
            setEditAssigneeSearchTerm('');
            setIsEditAssigneeDropdownOpen(false);
          }}
          width="448px"
          data-cy="edit-assignee-modal"
        >
          <Flex padding={4} gap={2} flexDirection="column">
            <Text textStyle="headingGroup">Change {editingRole.role}</Text>
            <Flex py={4} flexDirection="column">
              <Popover
                isOpen={isEditAssigneeDropdownOpen}
                onOpen={() => setIsEditAssigneeDropdownOpen(true)}
                onClose={() => {
                  setIsEditAssigneeDropdownOpen(false);
                  setEditAssigneeSearchTerm('');
                }}
                placement="bottom-start"
                matchWidth
              >
                <PopoverTrigger>
                  <Button
                    variant="unstyled"
                    width="100%"
                    minWidth="0"
                    height="40px"
                    px={4}
                    py="8px"
                    textAlign="left"
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    backgroundColor="inputFill"
                    color="inputContent"
                    border="1px solid"
                    borderWidth="1px"
                    borderStyle="solid"
                    borderColor={
                      isEditAssigneeDropdownOpen
                        ? 'inputBorderActive'
                        : 'inputBorder'
                    }
                    boxShadow="none"
                    borderRadius="small"
                    cursor="pointer"
                    _focus={{
                      boxShadow: 'none',
                      bg: 'inputFill',
                      borderColor: 'inputBorder'
                    }}
                    _focusVisible={{
                      layerStyle: 'focus',
                      boxShadow: 'none',
                      bg: 'inputFill',
                      borderColor: 'inputBorder'
                    }}
                    _hover={{
                      bg: 'inputFill',
                      borderColor: 'inputBorderHover'
                    }}
                    _active={{
                      bg: 'inputFill',
                      borderColor: 'inputBorderActive'
                    }}
                    data-testId="edit-assignee-select"
                  >
                    <Text
                      flex="1"
                      minWidth="0"
                      marginLeft={4}
                      isTruncated
                      textStyle="textContext"
                      color={
                        editingRole.users.find(
                          user => user.id === editSelectedAssigneeId
                        )
                          ? 'inherit'
                          : 'GrayText'
                      }
                    >
                      {editingRole.users.find(
                        user => user.id === editSelectedAssigneeId
                      )?.firstName ?? 'Select an assignee'}{' '}
                      {editingRole.users.find(
                        user => user.id === editSelectedAssigneeId
                      )?.lastName ?? ''}
                    </Text>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      style={{ flexShrink: 0, marginRight: 8 }}
                    />
                  </Button>
                </PopoverTrigger>
                <PopoverContent zIndex="popover" width="100%">
                  <PopoverBody p={2}>
                    <SearchInput
                      name="edit-assignee-search"
                      placeholder="Search by user name"
                      value={editAssigneeSearchTerm}
                      onChange={e => setEditAssigneeSearchTerm(e.target.value)}
                      onInputClear={() => setEditAssigneeSearchTerm('')}
                      data-testId="edit-assignee-search"
                      mb={2}
                    />
                    <Box maxH="200px" overflowY="auto">
                      {filteredEditAssignees.length > 0 ? (
                        filteredEditAssignees.map(user => {
                          const isAlreadyAssigned =
                            recommendationAssignees?.recommendationAssignees?.some(
                              item =>
                                item.role === editingRole.role &&
                                item.userId === user.id
                            ) ?? false;
                          return (
                            <Box
                              key={user.id}
                              as="button"
                              type="button"
                              disabled={isAlreadyAssigned}
                              width="100%"
                              p={2}
                              textAlign="left"
                              borderRadius="sm"
                              opacity={isAlreadyAssigned ? 0.5 : 1}
                              cursor={
                                isAlreadyAssigned ? 'not-allowed' : 'pointer'
                              }
                              _hover={{
                                backgroundColor: isAlreadyAssigned
                                  ? 'transparent'
                                  : 'actionSecondaryFill'
                              }}
                              onClick={() => {
                                setEditSelectedAssigneeId(user.id);
                                setIsEditAssigneeDropdownOpen(false);
                                setEditAssigneeSearchTerm('');
                              }}
                            >
                              {user.firstName} {user.lastName}
                            </Box>
                          );
                        })
                      ) : (
                        <Text
                          textStyle="textContext"
                          color="textSecondary"
                          p={2}
                        >
                          No users found
                        </Text>
                      )}
                    </Box>
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            </Flex>
            <Flex gap={2} marginTop={4} justifyContent="flex-end">
              <Button
                onClick={() => {
                  setEditingRole(null);
                  setEditSelectedAssigneeId('');
                  setEditAssigneeSearchTerm('');
                  setIsEditAssigneeDropdownOpen(false);
                }}
                disabled={manageSubmissionAssignmentMutation.isLoading}
                variant="secondary"
                data-cy="edit-assignee-exit-button"
              >
                Exit without changes
              </Button>
              <Button
                variant="primary"
                onClick={onSaveEditRole}
                disabled={
                  editSelectedAssigneeId === editingRole.assigneeId ||
                  manageSubmissionAssignmentMutation.isLoading
                }
                data-cy="edit-assignee-save-button"
              >
                Save changes
              </Button>
            </Flex>
          </Flex>
        </Modal>
      )}
      {!readOnly && assigningRole && (
        <Modal
          isOpen={!!assigningRole}
          onClose={() => setAssigningRole(null)}
          width="40rem"
          closeButtonProps={{
            hidden: true
          }}
        >
          <Flex
            flexDirection="column"
            backgroundColor="surfacePrimaryFill"
            p={4}
          >
            <Flex alignItems="center" justifyContent="space-between" mb={4}>
              <Text textStyle="headingGroup">Assign {assigningRole.role}</Text>
              <ButtonIcon
                icon={<FontAwesomeIcon icon={faXmark} />}
                ariaLabel="Close assign role"
                size="sm"
                variant="primary"
                onClick={() => setAssigningRole(null)}
              />
            </Flex>
            <Grid templateColumns="1fr 1fr" minH="260px">
              <Flex
                flexDirection="column"
                pr={3}
                borderRight="1px solid"
                borderColor="neutralPrimaryBorder"
              >
                <Text textStyle="headingLine" mb={2}>
                  Add User(s)
                </Text>
                <SearchInput
                  name="assign-to-search"
                  placeholder="Search by user name"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  onInputClear={() => setSearchTerm('')}
                  data-testId="assign-to-search"
                  mb={2}
                />
                <Box maxHeight="260px" overflowY="auto">
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map(user => {
                      return (
                        <Flex
                          key={user.id}
                          alignItems="center"
                          justifyContent="space-between"
                          gap={1}
                          py={1}
                          _hover={{ backgroundColor: 'surfaceSecondaryFill' }}
                          padding={2}
                        >
                          <Flex alignItems="center" gap={2} minW={0} flex={1}>
                            <Box position="relative" flexShrink={0}>
                              <Avatar
                                type="user"
                                shape="circle"
                                name={`${user.firstName} ${user.lastName}`}
                                width={8}
                                height={8}
                                bgColor={getAvatarBackground(
                                  assigningRole.role
                                )}
                                color="white"
                                size={'sm'}
                              />
                              {currentUser.id === user.id && (
                                <Flex
                                  position="absolute"
                                  top="-3px"
                                  right="-5px"
                                  width={4}
                                  height={4}
                                  alignItems="center"
                                  justifyContent="center"
                                  borderRadius="full"
                                  backgroundColor="green.300"
                                  color="green.900"
                                >
                                  <FontAwesomeIcon icon={faUser} fontSize={9} />
                                </Flex>
                              )}
                            </Box>
                            <Text textStyle="textContext" isTruncated>
                              {user.firstName} {user.lastName}
                            </Text>
                          </Flex>
                          <ButtonText
                            leftIcon={<FontAwesomeIcon icon={faPlus} />}
                            fontSize="xs"
                            disabled={
                              !canAddToAssigningRole ||
                              manageSubmissionAssignmentMutation.isLoading ||
                              recommendationAssignees?.recommendationAssignees?.some(
                                item =>
                                  item.role === assigningRole.role &&
                                  item.userId === user.id
                              )
                            }
                            onClick={() =>
                              onAssignSubmission(user.id, assigningRole.role)
                            }
                          >
                            Add
                          </ButtonText>
                        </Flex>
                      );
                    })
                  ) : (
                    <Box px={3} py={3}>
                      <Text textStyle="textContext" color="textSecondary">
                        No users found
                      </Text>
                    </Box>
                  )}
                </Box>
              </Flex>
              <Flex flexDirection="column" pl={3} minW={0}>
                <Flex justifyContent="space-between" alignItems="center" mb={2}>
                  <Text textStyle="headingLine">Currently Assigned</Text>
                  <Text textStyle="textContext" color="textSecondary">
                    ({assigningRoleAssignedCount}/{assigningRoleMaxLimit})
                  </Text>
                </Flex>
                {Divider}
                {currentlyAssignedUsers.length > 0 ? (
                  <Flex
                    flexDirection="column"
                    gap={1}
                    pt={2}
                    maxHeight="300px"
                    overflowY="auto"
                  >
                    {currentlyAssignedUsers.map(currentlyAssignedUser => (
                      <Flex
                        key={currentlyAssignedUser.id}
                        gap={2}
                        flexDirection="column"
                      >
                        <Flex alignItems="center" gap={2} py={1} minW={0}>
                          <Avatar
                            type="user"
                            shape="circle"
                            name={`${currentlyAssignedUser.firstName} ${currentlyAssignedUser.lastName}`}
                            width={8}
                            height={8}
                            bgColor={getAvatarBackground(assigningRole.role)}
                            color="white"
                            size={'sm'}
                          />
                          <Text textStyle="textContext" isTruncated flex={1}>
                            {currentlyAssignedUser.firstName}{' '}
                            {currentlyAssignedUser.lastName}
                          </Text>
                          <Flex gap={1} flexShrink={0}>
                            <Tooltip
                              hasArrow
                              placement="top"
                              label="Proxy user"
                            >
                              <Box>
                                <ProxyUser
                                  user={{
                                    firstName: currentlyAssignedUser.firstName,
                                    lastName: currentlyAssignedUser.lastName,
                                    userId: currentlyAssignedUser.id
                                  }}
                                  isDecisionMaker
                                  isButtonDisabled={
                                    currentUser.id ===
                                      currentlyAssignedUser.id ||
                                    proxyUserDisabled(currentlyAssignedUser.id)
                                  }
                                  role="board-member"
                                />
                              </Box>
                            </Tooltip>
                            <Tooltip
                              hasArrow
                              placement="top"
                              label="Send Email"
                            >
                              <Box>
                                <ButtonIcon
                                  icon={<FontAwesomeIcon icon={faEnvelope} />}
                                  size="small"
                                  variant="primary"
                                  onClick={() =>
                                    setOpenSendEmailModal({
                                      status: true,
                                      defaultEmail: [
                                        currentlyAssignedUser.email
                                      ]
                                    })
                                  }
                                  disabled={
                                    !currentlyAssignedUser.email ||
                                    currentUser.id ===
                                      currentlyAssignedUser.id ||
                                    submissionCurrentState ===
                                      'formalAcceptance'
                                  }
                                  ariaLabel="Send Mail"
                                />
                              </Box>
                            </Tooltip>
                            <Tooltip
                              hasArrow
                              placement="left"
                              label="Unassign Role"
                            >
                              <Box>
                                <ButtonIcon
                                  icon={
                                    <FontAwesomeIcon
                                      icon={faTrash}
                                      color="red"
                                    />
                                  }
                                  size="small"
                                  variant="primary"
                                  onClick={() =>
                                    setUnassigningRole({
                                      role: assigningRole.role,
                                      assigneeId: currentlyAssignedUser.id,
                                      assigneeName: `${currentlyAssignedUser.firstName} ${currentlyAssignedUser.lastName}`
                                    })
                                  }
                                  disabled={
                                    !isDecisionMaker ||
                                    isRevisionPhase ||
                                    (assigningRoleAssignedCount <= 1 &&
                                      findRoleConfig(assigningRole.role)
                                        ?.roleLevel === 1)
                                  }
                                  ariaLabel="Unassign Role"
                                />
                              </Box>
                            </Tooltip>
                          </Flex>
                        </Flex>
                        {Divider}
                      </Flex>
                    ))}
                  </Flex>
                ) : (
                  <OutlineCard minHeight="300px" mt={2}>
                    <Flex
                      justifyContent="center"
                      alignItems="center"
                      display="flex"
                      height="100%"
                    >
                      <Text textStyle="textContext" padding={4}>
                        No one has been assigned yet. To make someone an ‘
                        {assigningRole.role}’ click <b>+Add</b> next to their
                        name.
                      </Text>
                    </Flex>
                  </OutlineCard>
                )}
              </Flex>
            </Grid>
          </Flex>
        </Modal>
      )}
      <ChakraAccordion
        allowToggle
        defaultIndex={defaultExpanded ? 0 : undefined}
        width="100%"
      >
        <AccordionItem
          border="1px solid"
          borderColor="neutralPrimaryBorder"
          borderRadius="4px"
          overflow="hidden"
        >
          <AccordionButton py={4} px={3} bgColor="surfaceSecondaryFill">
            <Flex
              width="100%"
              alignItems="center"
              justifyContent="space-between"
            >
              <Text textStyle="headingLine">Editorial Assignee(s)</Text>
              <AccordionIcon fontSize="sm" />
            </Flex>
          </AccordionButton>
          <AccordionPanel p={0} background="surfacePrimaryFill">
            <ChakraAccordion allowMultiple>
              {rolesAssignments.map(({ role, users }) => {
                const assignedUserIds = new Set(
                  recommendationAssignees?.recommendationAssignees
                    ?.filter(item => item.role === role)
                    .map(item => item.userId) ?? []
                );
                const assignedUsers = users.filter(user =>
                  assignedUserIds.has(user.id)
                );
                const assigneeId = assignedUsers[0]?.id;
                const isValidUser = users.find(item => item.id === assigneeId);
                const roleConfig = submissionContext?.config.cocConfig?.find(
                  item => item.role === role
                );
                const roleMaxLimit = roleConfig?.roleMaxLimit ?? 1;
                const assignedCount =
                  recommendationAssignees?.recommendationAssignees?.filter(
                    item => item.role === role
                  ).length ?? 0;
                const canAddMoreAssignees = assignedCount < roleMaxLimit;
                const canAssign =
                  isDecisionMaker &&
                  isCoCPhase &&
                  (cocConfigOwner === role || isOwnerAssign) &&
                  (!isChecklistPhase ||
                    cocConfigOwner === role ||
                    (isInitialAssessmentPhase &&
                      !hasApproveForPeerReviewDecision));
                const maxVisibleAssigneeAvatars = 7;
                const visibleAssignedUsers = assignedUsers.slice(
                  0,
                  maxVisibleAssigneeAvatars
                );
                const hiddenAssigneeCount =
                  assignedUsers.length - visibleAssignedUsers.length;
                const assigneeAvatars = visibleAssignedUsers.map(user => (
                  <Tooltip
                    key={user.id}
                    label={`${user.firstName} ${user.lastName}`}
                    hasArrow
                  >
                    <Box position="relative" flexShrink={0} mr={-2}>
                      <Avatar
                        type="user"
                        shape="circle"
                        name={`${user.firstName} ${user.lastName}`}
                        width={8}
                        height={8}
                        bgColor={getAvatarBackground(role)}
                        color="white"
                        cursor={'pointer'}
                        size={'sm'}
                      />
                      {currentUser.id === user.id && (
                        <Flex
                          position="absolute"
                          top="-4px"
                          right="-7px"
                          width={4}
                          height={4}
                          alignItems="center"
                          justifyContent="center"
                          borderRadius="full"
                          backgroundColor="green.300"
                          color="green.900"
                        >
                          <FontAwesomeIcon icon={faUser} fontSize={9} />
                        </Flex>
                      )}
                    </Box>
                  </Tooltip>
                ));
                return cocConfig?.some(item => item.role === role) ? (
                  <AccordionItem
                    key={role}
                    border="none"
                    borderTop="1px solid"
                    borderColor="neutralPrimaryBorder"
                  >
                    {({ isExpanded }) => (
                      <>
                        <Flex alignItems="center" px={3} py={2} gap={2}>
                          <Flex
                            alignItems="center"
                            gap={1}
                            width="100%"
                            textAlign="left"
                          >
                            <Text textStyle="headingLine">{role}</Text>
                            <Text textStyle="textContext" color="textSecondary">
                              ({assignedCount}/{roleMaxLimit})
                            </Text>
                            {!isExpanded && assignedUsers.length > 0 && (
                              <Flex gap={0} alignItems="center" ml={1}>
                                {assigneeAvatars}
                                {hiddenAssigneeCount > 0 && (
                                  <Tooltip
                                    label={
                                      <Box
                                        as="ul"
                                        pl={4}
                                        m={0}
                                        textAlign="left"
                                      >
                                        {assignedUsers
                                          .slice(maxVisibleAssigneeAvatars)
                                          .map(user => (
                                            <Box as="li" key={user.id}>
                                              {user.firstName} {user.lastName}
                                            </Box>
                                          ))}
                                      </Box>
                                    }
                                    hasArrow
                                  >
                                    <Box>
                                      <Avatar
                                        type="user"
                                        shape="circle"
                                        name={`+ ${hiddenAssigneeCount}`}
                                        width={8}
                                        height={8}
                                        bgColor={getAvatarBackground(role)}
                                        color="white"
                                        size={'sm'}
                                      />
                                    </Box>
                                  </Tooltip>
                                )}
                              </Flex>
                            )}
                          </Flex>
                          <Flex alignItems="center">
                            {!readOnly && (
                              <>
                                <Tooltip
                                  label="Assign Role"
                                  hasArrow
                                  isDisabled={
                                    !canAssign ||
                                    !canAddMoreAssignees ||
                                    isAnyModalOpen
                                  }
                                >
                                  <Box>
                                    <ButtonText
                                      leftIcon={
                                        <FontAwesomeIcon icon={faPlus} />
                                      }
                                      fontSize="sm"
                                      onClick={() =>
                                        setAssigningRole({
                                          role,
                                          users,
                                          assigneeId,
                                          isValidUser: !!isValidUser
                                        })
                                      }
                                      disabled={
                                        !canAssign || !canAddMoreAssignees
                                      }
                                    >
                                      Add
                                    </ButtonText>
                                  </Box>
                                </Tooltip>
                                <DividerVertical />
                              </>
                            )}
                            <AccordionButton>
                              <Box ml="auto">
                                <AccordionIcon fontSize="sm" />
                              </Box>
                            </AccordionButton>
                          </Flex>
                        </Flex>
                        <AccordionPanel px={3} pb={2} pt={0}>
                          {assignedUsers.length > 0 ? (
                            <Flex flexDirection="column" gap={1} mt={1}>
                              {assignedUsers.map(user => (
                                <Flex
                                  key={user.id}
                                  alignItems="center"
                                  gap={2}
                                  minW={0}
                                >
                                  <Box position="relative" flexShrink={0}>
                                    <Avatar
                                      type="user"
                                      shape="circle"
                                      name={`${user.firstName} ${user.lastName}`}
                                      width={8}
                                      height={8}
                                      bgColor={getAvatarBackground(role)}
                                      color="white"
                                      size={'sm'}
                                    />
                                    {currentUser.id === user.id && (
                                      <Flex
                                        position="absolute"
                                        top="-3px"
                                        right="-5px"
                                        width={4}
                                        height={4}
                                        alignItems="center"
                                        justifyContent="center"
                                        borderRadius="full"
                                        backgroundColor="green.300"
                                        color="green.900"
                                      >
                                        <FontAwesomeIcon
                                          icon={faUser}
                                          fontSize={9}
                                        />
                                      </Flex>
                                    )}
                                  </Box>
                                  <Text
                                    textStyle="textBody"
                                    isTruncated
                                    flex="1"
                                  >
                                    {user.firstName} {user.lastName}
                                  </Text>
                                  {!readOnly && (
                                    <Flex gap={1} flexShrink={0}>
                                      <Tooltip
                                        key={`${role}-edit-${String(
                                          isAnyModalOpen
                                        )}-${user.id}`}
                                        isDisabled={isAnyModalOpen}
                                        hasArrow
                                        shouldWrapChildren
                                        placement="top"
                                        label="Edit"
                                        borderRadius={4}
                                      >
                                        <ButtonIcon
                                          icon={
                                            <FontAwesomeIcon
                                              icon={faPenToSquare}
                                            />
                                          }
                                          size="small"
                                          variant="primary"
                                          onClick={() => {
                                            setEditingRole({
                                              role,
                                              users,
                                              assigneeId: user.id,
                                              isValidUser: !!isValidUser
                                            });
                                            setEditSelectedAssigneeId(user.id);
                                          }}
                                          disabled={isRevisionPhase||!isDecisionMaker}
                                          ariaLabel="Edit Role"
                                        />
                                      </Tooltip>
                                      <Tooltip
                                        key={`${role}-proxy-${String(
                                          isAnyModalOpen
                                        )}-${user.id}`}
                                        isDisabled={isAnyModalOpen}
                                        hasArrow
                                        shouldWrapChildren
                                        placement="top"
                                        label="Proxy user"
                                        borderRadius={4}
                                      >
                                        <ProxyUser
                                          user={{
                                            firstName: user.firstName,
                                            lastName: user.lastName,
                                            userId: user.id
                                          }}
                                          isDecisionMaker
                                          isButtonDisabled={
                                            currentUser.id === user.id ||
                                            proxyUserDisabled(user.id)
                                          }
                                          role={'board-member'}
                                        />
                                      </Tooltip>
                                      <Tooltip
                                        key={`${role}-email-${String(
                                          isAnyModalOpen
                                        )}-${user.id}`}
                                        isDisabled={isAnyModalOpen}
                                        hasArrow
                                        shouldWrapChildren
                                        placement="top"
                                        label="Send Email"
                                        borderRadius={4}
                                      >
                                        <ButtonIcon
                                          icon={
                                            <FontAwesomeIcon
                                              icon={faEnvelope}
                                            />
                                          }
                                          size="small"
                                          variant="primary"
                                          onClick={() => {
                                            setOpenSendEmailModal({
                                              status: true,
                                              defaultEmail: [user.email]
                                            });
                                          }}
                                          disabled={
                                            !user.email ||
                                            currentUser.id === user.id ||
                                            submissionCurrentState ===
                                              'formalAcceptance'
                                          }
                                          ariaLabel="Send Mail"
                                        />
                                      </Tooltip>
                                      <Tooltip
                                        key={`${role}-unassign-${String(
                                          isAnyModalOpen
                                        )}-${user.id}`}
                                        isDisabled={isAnyModalOpen}
                                        hasArrow
                                        shouldWrapChildren
                                        placement="left"
                                        label="Unassign Role"
                                        borderRadius={4}
                                      >
                                        <ButtonIcon
                                          icon={
                                            <FontAwesomeIcon
                                              icon={faTrash}
                                              color="red"
                                            />
                                          }
                                          onClick={() => {
                                            setUnassigningRole({
                                              role,
                                              assigneeId: user.id,
                                              assigneeName: `${user.firstName} ${user.lastName}`
                                            });
                                          }}
                                          size="small"
                                          variant="primary"
                                          disabled={
                                            !isDecisionMaker ||
                                            isRevisionPhase ||
                                            (assignedCount <= 1 &&
                                              roleConfig?.roleLevel === 1)
                                          }
                                          ariaLabel="Unassign Role"
                                        />
                                      </Tooltip>
                                    </Flex>
                                  )}
                                </Flex>
                              ))}
                            </Flex>
                          ) : (
                            <Text textStyle="textContext" color="textSecondary">
                              No assignee yet
                            </Text>
                          )}
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                ) : null;
              })}
            </ChakraAccordion>
          </AccordionPanel>
        </AccordionItem>
      </ChakraAccordion>

      {!readOnly && openSendEmailModal.status && (
        <ComposeEmailModal
          setOpenSendEmailModal={setOpenSendEmailModal}
          email={openSendEmailModal.defaultEmail}
        />
      )}
    </>
  );
};
